// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useCallback, useMemo, useState, useRef } from 'react';

import { type UserId, type User, useEvolveGet } from '@evolvemep/api-ts';

import { useGeneralContext } from 'helpers/useGeneralContext';
import { validateUserNameOrEmail } from 'helpers/userHelpers';

type CachedUser = { completeName: string; email: string };

type UsersInfoContextType = {
  loading: boolean;
  fetchUserInfo: (...userIds: UserId[]) => Promise<void>;
  userInfo: Record<UserId, CachedUser>;
};

const UsersInfoContext = React.createContext<UsersInfoContextType | undefined>(undefined);

export const UsersInfoProvider = ({ children }: { children: ReactNode }) => {
  const [userInfo, setUserInfo] = useState<Record<UserId, CachedUser>>({});

  const { apiCall: getUserApiCall, loading } = useEvolveGet('admin/user/:userId');

  const promises = useRef<Record<UserId, Promise<User> | undefined>>({});

  const fetchUserInfo = useCallback<UsersInfoContextType['fetchUserInfo']>(
    async (...userIds) => {
      await Promise.allSettled(
        userIds.map((userId) =>
          (promises.current[userId] ??= getUserApiCall({
            url: `admin/user/${userId}`,
          }).then((user) => {
            const newRecord: CachedUser = { completeName: validateUserNameOrEmail(user), email: user.userEmail };
            setUserInfo((u) => ({
              ...u,
              [userId]: newRecord,
            }));
            return user;
          })).catch((e) => {
            promises.current[userId] = undefined;
            // oxlint-disable-next-line no-console
            console.error('Could not fetch user info', userId, e);
          }),
        ),
      );
    },
    [getUserApiCall],
  );

  const value = useMemo(
    () => ({
      loading,
      fetchUserInfo,
      userInfo,
    }),
    [fetchUserInfo, loading, userInfo],
  );

  return <UsersInfoContext value={value}>{children}</UsersInfoContext>;
};

export const useUsersInfo = () => useGeneralContext(UsersInfoContext, 'UsersInfo');
